import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, 
  Clock, Trophy, AlertCircle, FileDown, User,
  Play, RotateCcw, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE;

// Authorized admin email
const AUTHORIZED_ADMIN = 'credit@inspiredfunding.org';

export default function TakeAssessment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { type, id } = useParams(); // type: 'quiz' or 'final', id: module_id for quiz
  const [searchParams] = useSearchParams();
  
  // States
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [participantName, setParticipantName] = useState('');
  const [examStarted, setExamStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [timeStarted, setTimeStarted] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const lang = user?.training_language || 'es';
  const t = (en, es) => lang === 'es' ? es : en;

  // Check authorization
  const isAuthorized = user?.role === 'admin' && user?.email === AUTHORIZED_ADMIN;

  useEffect(() => {
    if (!isAuthorized) {
      toast.error(t('Access denied', 'Acceso denegado'));
      navigate('/training/admin/assessments');
      return;
    }
    fetchAssessment();
  }, [type, id]);

  const fetchAssessment = async () => {
    try {
      let endpoint;
      if (type === 'quiz') {
        const searchParams = new URLSearchParams(window.location.search);
        const quizNumber = searchParams.get('quiz_number') || '1';
        endpoint = `${API}/training/admin/get-quiz/${id}?quiz_number=${quizNumber}`;
      } else {
        endpoint = `${API}/training/admin/get-final-exam`;
      }
      
      const response = await axios.get(endpoint);
      setAssessment(response.data);
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error(t('Error loading assessment', 'Error al cargar evaluación'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = () => {
    if (!participantName.trim()) {
      toast.error(t('Please enter participant name', 'Por favor ingrese el nombre del participante'));
      return;
    }
    setExamStarted(true);
    setTimeStarted(new Date());
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleSelectAnswer = (questionIndex, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleNext = () => {
    if (currentQuestion < (assessment?.questions?.length || 0) - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const questions = assessment?.questions || [];
    const totalQuestions = questions.length;
    
    // Calculate results
    let correct = 0;
    const feedback = [];
    
    questions.forEach((q, idx) => {
      const userAnswer = answers[idx];
      const correctAnswer = q.correct_answer_index ?? q.correct_answer ?? 0;
      const isCorrect = userAnswer === correctAnswer;
      
      if (isCorrect) correct++;
      
      feedback.push({
        questionIndex: idx,
        question: q.question_es || q.question_en,
        userAnswer: userAnswer,
        correctAnswer: correctAnswer,
        isCorrect: isCorrect,
        options: q.options_es || q.options_en,
        explanation: q.explanation_es || q.explanation_en
      });
    });
    
    const score = totalQuestions > 0 ? (correct / totalQuestions) * 100 : 0;
    const passed = score >= (assessment?.passing_score || 80);
    const timeEnded = new Date();
    const timeTaken = Math.round((timeEnded - timeStarted) / 60000); // minutes
    
    const resultData = {
      participantName,
      assessmentType: type,
      assessmentId: type === 'quiz' ? id : 'final-exam',
      assessmentTitle: assessment?.title_es || assessment?.title_en,
      totalQuestions,
      correctAnswers: correct,
      score: Math.round(score * 10) / 10,
      passed,
      passingScore: assessment?.passing_score || 80,
      timeStarted: timeStarted.toISOString(),
      timeEnded: timeEnded.toISOString(),
      timeTakenMinutes: timeTaken,
      feedback
    };
    
    setResults(resultData);
    setShowResults(true);
    
    // Save results to database
    try {
      await axios.post(`${API}/training/admin/save-assessment-result`, resultData);
    } catch (error) {
      console.error('Error saving results:', error);
    }
  };

  const handleDownloadResults = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/training/admin/download-assessment-result`,
        results,
        { responseType: 'blob', timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `Resultado_${type === 'quiz' ? 'Quiz' : 'ExamenFinal'}_${participantName.replace(/\s+/g, '_')}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('Results downloaded!', '¡Resultados descargados!'));
    } catch (error) {
      console.error('Error downloading results:', error);
      toast.error(t('Error downloading results', 'Error al descargar resultados'));
    } finally {
      setDownloading(false);
    }
  };

  const handleRetake = () => {
    setExamStarted(false);
    setShowResults(false);
    setResults(null);
    setAnswers({});
    setCurrentQuestion(0);
    setParticipantName('');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('Loading assessment...', 'Cargando evaluación...')}</p>
        </div>
      </div>
    );
  }

  // No assessment found
  if (!assessment) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-red-700">
              {t('Assessment not found', 'Evaluación no encontrada')}
            </h2>
            <Button onClick={() => navigate('/training/admin/assessments')} className="mt-4">
              {t('Back to Assessments', 'Volver a Evaluaciones')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const questions = assessment.questions || [];
  const totalQuestions = questions.length;

  // Results view
  if (showResults && results) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className={`${results.passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <CardHeader className="text-center">
              {results.passed ? (
                <Trophy className="h-20 w-20 mx-auto text-yellow-500 mb-4" />
              ) : (
                <XCircle className="h-20 w-20 mx-auto text-red-500 mb-4" />
              )}
              <CardTitle className={`text-3xl ${results.passed ? 'text-green-700' : 'text-red-700'}`}>
                {results.passed 
                  ? t('Congratulations!', '¡Felicidades!') 
                  : t('Not Passed', 'No Aprobado')}
              </CardTitle>
              <CardDescription className="text-lg">
                {t('Participant:', 'Participante:')} <strong>{results.participantName}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-3xl font-bold text-primary">{results.score}%</p>
                  <p className="text-sm text-muted-foreground">{t('Score', 'Puntaje')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-3xl font-bold text-primary">{results.correctAnswers}/{results.totalQuestions}</p>
                  <p className="text-sm text-muted-foreground">{t('Correct', 'Correctas')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-3xl font-bold text-primary">{results.passingScore}%</p>
                  <p className="text-sm text-muted-foreground">{t('Required', 'Requerido')}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-3xl font-bold text-primary">{results.timeTakenMinutes} min</p>
                  <p className="text-sm text-muted-foreground">{t('Time', 'Tiempo')}</p>
                </div>
              </div>

              <Progress value={results.score} className="h-4" />

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center">
                <Button 
                  onClick={handleDownloadResults}
                  disabled={downloading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {downloading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <FileDown className="h-4 w-4 mr-2" />
                  )}
                  {t('Download Results (PDF)', 'Descargar Resultados (PDF)')}
                </Button>
                <Button variant="outline" onClick={handleRetake}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t('New Participant', 'Nuevo Participante')}
                </Button>
                <Button variant="outline" onClick={() => navigate('/training/admin/assessments')}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('Back to Assessments', 'Volver a Evaluaciones')}
                </Button>
              </div>

              {/* Detailed Feedback */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">{t('Detailed Results', 'Resultados Detallados')}</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {results.feedback.map((item, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${item.isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-start gap-2">
                        {item.isCorrect ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{idx + 1}. {item.question}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('Your answer:', 'Tu respuesta:')} {item.options[item.userAnswer] || t('Not answered', 'Sin respuesta')}
                            {!item.isCorrect && (
                              <span className="text-green-600 ml-2">
                                | {t('Correct:', 'Correcta:')} {item.options[item.correctAnswer]}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Start screen
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-2xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/training/admin/assessments')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back to Assessments', 'Volver a Evaluaciones')}
          </Button>

          <Card>
            <CardHeader className="text-center">
              <ClipboardList className="h-16 w-16 mx-auto text-primary mb-4" />
              <CardTitle className="text-2xl">
                {assessment.title_es || assessment.title_en}
              </CardTitle>
              <CardDescription>
                {type === 'quiz' 
                  ? t('Module Quiz - In-Person Assessment', 'Quiz del Módulo - Evaluación Presencial')
                  : t('Final Certification Exam - In-Person Assessment', 'Examen Final de Certificación - Evaluación Presencial')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Assessment Info */}
              <div className="bg-slate-100 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('Questions:', 'Preguntas:')}</span>
                  <span className="font-medium">{totalQuestions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('Passing Score:', 'Puntaje para Aprobar:')}</span>
                  <span className="font-medium">{assessment.passing_score || 80}%</span>
                </div>
                {assessment.time_limit_minutes && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('Time Limit:', 'Tiempo Límite:')}</span>
                    <span className="font-medium">{assessment.time_limit_minutes} min</span>
                  </div>
                )}
              </div>

              {/* Participant Name Input */}
              <div className="space-y-2">
                <Label htmlFor="participantName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('Participant Name', 'Nombre del Participante')} *
                </Label>
                <Input
                  id="participantName"
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  placeholder={t('Enter full name...', 'Ingrese nombre completo...')}
                  className="text-lg"
                />
              </div>

              {/* Start Button */}
              <Button 
                onClick={handleStartExam} 
                className="w-full" 
                size="lg"
                disabled={!participantName.trim()}
              >
                <Play className="h-5 w-5 mr-2" />
                {t('Start Assessment', 'Iniciar Evaluación')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Exam in progress
  const currentQ = questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Badge variant="outline" className="mb-1">
              {t('Participant:', 'Participante:')} {participantName}
            </Badge>
            <h2 className="text-lg font-semibold">
              {assessment.title_es || assessment.title_en}
            </h2>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">
              {t('Question', 'Pregunta')} {currentQuestion + 1} / {totalQuestions}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('Answered:', 'Respondidas:')} {answeredCount} / {totalQuestions}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <Progress value={((currentQuestion + 1) / totalQuestions) * 100} className="h-2 mb-6" />

        {/* Question Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-medium mb-6">
              {currentQuestion + 1}. {currentQ?.question_es || currentQ?.question_en}
            </h3>

            <RadioGroup
              value={answers[currentQuestion]?.toString()}
              onValueChange={(value) => handleSelectAnswer(currentQuestion, parseInt(value))}
              className="space-y-3"
            >
              {(currentQ?.options_es || currentQ?.options_en || []).map((option, idx) => (
                <div
                  key={idx}
                  className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    answers[currentQuestion] === idx
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => handleSelectAnswer(currentQuestion, idx)}
                >
                  <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                  <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                    <span className="font-medium mr-2">{String.fromCharCode(65 + idx)})</span>
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Previous', 'Anterior')}
          </Button>

          {currentQuestion === totalQuestions - 1 ? (
            <Button
              onClick={handleSubmit}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {t('Submit & Grade', 'Enviar y Calificar')}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              {t('Next', 'Siguiente')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question Navigator */}
        <div className="mt-6 p-4 bg-white rounded-lg border">
          <p className="text-sm text-muted-foreground mb-3">{t('Jump to question:', 'Ir a pregunta:')}</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                  idx === currentQuestion
                    ? 'bg-primary text-white'
                    : answers[idx] !== undefined
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-slate-100 hover:bg-slate-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
